import { useState } from 'react';

import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonItem,
  IonLabel,
  IonSkeletonText,
  IonThumbnail,
  IonToast
} from '@ionic/react';
import { checkmark } from 'ionicons/icons';

import styles from './CompleteCard.module.scss';

import { State } from '../../constants/state';
import { Service } from '../../services/service';
import {
  Organization,
  Repository,
  Teammate,
  User
} from '../../types/services.types';
import { ServiceLoader } from '../ServiceLoader/ServiceLoader';
import { SignedInHeader } from '../SignedInHeader/SignedInHeader';
import { TeammateList } from '../TeammateList/TeammateList';
import { TeammatesLoading } from '../TeammatesLoading/TeammatesLoading';

export interface CompleteCardProps {
  deepLink?: string;
  ide?: string;
  loading: boolean;
  onInviteClicked: (email: string) => Promise<void>;
  organization?: Organization;
  repository?: Repository;
  scheme?: string;
  service: Service;
  teammates?: Teammate[];
  token?: string;
  tokenInput?: boolean;
  user?: User;
}

export const CompleteCard = ({
  loading,
  service,
  user,
  organization,
  repository,
  teammates,
  ide,
  scheme,
  deepLink,
  token,
  onInviteClicked
}: CompleteCardProps) => {
  const [linkCopied, setLinkCopied] = useState(false);

  const copyTokenClicked = () => {
    navigator.clipboard.writeText(deepLink!);
    setLinkCopied(true);
  };

  if (loading) {
    return (
      <div className={styles.cardContainer}>
        <ServiceLoader service={service.id} state={State.Loading} />
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>
              <IonSkeletonText
                animated={true}
                style={{ width: '200px' }}
              ></IonSkeletonText>
            </IonCardTitle>
            <IonItem lines="none">
              <IonThumbnail slot="start">
                <IonSkeletonText animated={true}></IonSkeletonText>
              </IonThumbnail>
              <IonLabel>
                <h2>
                  <IonSkeletonText
                    animated={true}
                    style={{ width: '300px' }}
                  ></IonSkeletonText>
                </h2>
                <p>
                  <IonSkeletonText
                    animated={true}
                    style={{ width: '150px' }}
                  ></IonSkeletonText>
                </p>
              </IonLabel>
            </IonItem>
          </IonCardHeader>

          <IonCardContent>
            <TeammatesLoading />
          </IonCardContent>
        </IonCard>
      </div>
    );
  } else if (user && organization && teammates && scheme && deepLink) {
    return (
      <div className={styles.cardContainer}>
        <ServiceLoader service={service.id} state={State.Successful} />
        <IonCard>
          <IonCardHeader>
            <SignedInHeader
              photoUrl={user.photoURL}
              name={user.displayName}
              login={user.login}
              token={token}
              service={service}
            />
          </IonCardHeader>

          <IonCardContent>
            <TeammateList
              photoUrl={organization.photoURL}
              name={repository?.displayName || organization.displayName}
              teammates={teammates}
              token={token}
              service={service}
              onInviteClicked={onInviteClicked}
            />
          </IonCardContent>

          <section className={styles.footer}>
            <IonButton
              color="primary"
              fill="solid"
              expand="block"
              href={deepLink}
            >
              Authorize {ide}
            </IonButton>

            <hr />

            <IonLabel className={styles.deepLink}>
              <a className="btn" onClick={copyTokenClicked}>
                Deep link not working?
              </a>
            </IonLabel>
          </section>
        </IonCard>

        <IonToast
          isOpen={linkCopied}
          message={`The link has been copied to your clipboard, run the Open Deep Link Manually ${scheme.startsWith('jetbrains') ? `action` : `command`} in ${ide}`}
          onDidDismiss={() => setLinkCopied(false)}
          duration={3000}
          color="success"
          position="bottom"
          icon={checkmark}
          style={{ color: 'white' }}
        />
      </div>
    );
  }
};
